import React, { useState, useEffect } from 'react';
import { Container, Form, Card, Button, Alert, InputGroup } from 'react-bootstrap';
import { EyeFill, EyeSlashFill } from 'react-bootstrap-icons';
import { settingsAPI } from '../api/settingsAPI';
import { vendorAPI } from '../api/vendorAPI';
import { usePermissions } from '../contexts/PermissionContext';

const AI_PROVIDERS = [
    { value: '', label: '-- Select Provider --' },
    { value: 'anthropic', label: 'Anthropic (Claude)' },
    { value: 'openai', label: 'OpenAI' },
    { value: 'venice', label: 'Venice.ai' },
    { value: 'custom', label: 'Custom' },
];

const AI_MODELS = {
    anthropic: [
        { value: 'claude-sonnet-4-6', label: 'Claude Sonnet 4.6 — balanced' },
        { value: 'claude-opus-4-6', label: 'Claude Opus 4.6 — capable' },
        { value: 'claude-opus-4-7', label: 'Claude Opus 4.7 — most capable' },
    ],
    openai: [
        { value: 'gpt-4.1-nano', label: 'GPT-4.1 Nano — ultra-budget' },
        { value: 'gpt-4.1-mini', label: 'GPT-4.1 Mini — budget' },
        { value: 'gpt-4.1', label: 'GPT-4.1 — balanced' },
        { value: 'gpt-4o', label: 'GPT-4o — production' },
    ],
    venice: [
        { value: 'deepseek-v4-flash', label: 'DeepSeek V4 Flash — budget' },
        { value: 'deepseek-v4-pro', label: 'DeepSeek V4 Pro — capable' },
    ],
};

function SystemSettingsScreen() {
    const { hasPermission, loading: permissionsLoading } = usePermissions();
    const [settings, setSettings] = useState({
        enable_warehouse_transit: true,
        company_name: '',
        default_manufacturer: '',
        ai_provider: '',
        ai_model: '',
        ai_api_key: '',
        ai_api_base_url: '',
    });
    const [vendors, setVendors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [alert, setAlert] = useState(null);
    const [aiApiKeySet, setAiApiKeySet] = useState(false);
    const [showApiKey, setShowApiKey] = useState(false);

    useEffect(() => {
        loadSettings();
        loadVendors();
    }, []);

    const loadSettings = async () => {
        try {
            setLoading(true);
            const data = await settingsAPI.get();
            setAiApiKeySet(data.ai_api_key_set || false);
            setSettings({ ...data, ai_api_key: '' });
        } catch (error) {
            setAlert({ variant: 'danger', message: 'Failed to load settings' });
        } finally {
            setLoading(false);
        }
    };

    const loadVendors = async () => {
        try {
            const data = await vendorAPI.getAll();
            setVendors(data);
        } catch (error) {
            console.error('Failed to load vendors:', error);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const payload = { ...settings };
            if (!payload.ai_api_key) {
                delete payload.ai_api_key;
            }
            await settingsAPI.update(payload);
            if (settings.ai_api_key) {
                setAiApiKeySet(true);
                setSettings(prev => ({ ...prev, ai_api_key: '' }));
            }
            setAlert({ variant: 'success', message: 'Settings updated successfully' });
        } catch (error) {
            setAlert({ variant: 'danger', message: 'Failed to update settings' });
        }
    };

    const handleCheckboxChange = (e) => {
        const { name, checked } = e.target;
        setSettings(prev => ({
            ...prev,
            [name]: checked
        }));
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        if (name === 'ai_provider') {
            const models = AI_MODELS[value] || [];
            setSettings(prev => ({
                ...prev,
                ai_provider: value || null,
                ai_model: models.length > 0 ? models[0].value : '',
            }));
            return;
        }
        setSettings(prev => ({
            ...prev,
            [name]: value || null
        }));
    };

    if (permissionsLoading || loading) {
        return (
            <Container className="py-3">
                <div className="text-center">Loading...</div>
            </Container>
        );
    }

    if (!hasPermission('system.settings.view')) {
        return (
            <Container className="py-3">
                <Alert variant="danger">
                    You don&apos;t have permission to view system settings.
                </Alert>
            </Container>
        )
    }

    const canModify = hasPermission('system.settings.modify');

    return (
        <Container className="py-3">
            <h1 className="mb-4">System Settings</h1>
            {alert && (
                <Alert
                    variant={alert.variant}
                    onClose={() => setAlert(null)}
                    dismissible
                >
                    {alert.message}
                </Alert>
            )}
            {!canModify && (
                <Alert variant="info">
                    You can view settings but don&apos;t have permission to modify them.
                </Alert>
            )}
            <Form onSubmit={handleSubmit}>
                <Card className="mb-4">
                    <Card.Header>Company Information</Card.Header>
                    <Card.Body>
                        <Form.Group className="mb-3">
                            <Form.Label>Company Name</Form.Label>
                            <Form.Control
                                type="text"
                                name="company_name"
                                value={settings.company_name || ''}
                                onChange={handleInputChange}
                                disabled={!canModify}
                                placeholder="Enter company name"
                            />
                            <Form.Text className="text-muted">
                                Company name for general use (reports, labels, etc.)
                            </Form.Text>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Default Manufacturer</Form.Label>
                            <Form.Select
                                name="default_manufacturer"
                                value={settings.default_manufacturer || ''}
                                onChange={handleInputChange}
                                disabled={!canModify}
                            >
                                <option value="">-- Select Vendor --</option>
                                {vendors.map((vendor) => (
                                    <option key={vendor._id} value={vendor._id}>
                                        {vendor.name}
                                    </option>
                                ))}
                            </Form.Select>
                            <Form.Text className="text-muted">
                                Default vendor to use as manufacturer for in-house manufactured products
                            </Form.Text>
                        </Form.Group>
                    </Card.Body>
                </Card>

                <Card className="mb-4">
                    <Card.Header>Warehouse Settings</Card.Header>
                    <Card.Body>
                        <Form.Group className="mb-3">
                            <Form.Check
                                type="switch"
                                id="enable_warehouse_transit"
                                name="enable_warehouse_transit"
                                label="Enable Warehouse Transit"
                                checked={settings.enable_warehouse_transit}
                                onChange={handleCheckboxChange}
                                disabled={!canModify}
                            />
                            <Form.Text className="text-muted">
                                When enabled, items must go through transit when moving between warehouses.
                                When disabled, items can be moved directly between warehouses.
                            </Form.Text>
                        </Form.Group>
                    </Card.Body>
                </Card>

                <Card className="mb-4">
                    <Card.Header>AI Assistant</Card.Header>
                    <Card.Body>
                        <Form.Group className="mb-4" controlId="ai_provider">
                            <Form.Label>Provider</Form.Label>
                            <Form.Select
                                name="ai_provider"
                                value={settings.ai_provider || ''}
                                onChange={handleInputChange}
                                disabled={!canModify}
                            >
                                {AI_PROVIDERS.map(p => (
                                    <option key={p.value} value={p.value}>{p.label}</option>
                                ))}
                            </Form.Select>
                        </Form.Group>

                        <Form.Group className="mb-4" controlId="ai_model">
                            <Form.Label>Model</Form.Label>
                            {AI_MODELS[settings.ai_provider] ? (
                                <Form.Select
                                    name="ai_model"
                                    value={settings.ai_model || ''}
                                    onChange={handleInputChange}
                                    disabled={!canModify}
                                >
                                    {AI_MODELS[settings.ai_provider].map(m => (
                                        <option key={m.value} value={m.value}>{m.label}</option>
                                    ))}
                                </Form.Select>
                            ) : (
                                <Form.Control
                                    type="text"
                                    name="ai_model"
                                    value={settings.ai_model || ''}
                                    onChange={handleInputChange}
                                    disabled={!canModify}
                                    placeholder="e.g. my-model-name"
                                    autoComplete="one-time-code"
                                    spellCheck="false"
                                />
                            )}
                        </Form.Group>

                        <Form.Group className="mb-4" controlId="ai_api_key">
                            <Form.Label>
                                API Key
                                {aiApiKeySet && (
                                    <small className="text-success ms-2">(configured)</small>
                                )}
                            </Form.Label>
                            <InputGroup>
                                <Form.Control
                                    type={showApiKey ? 'text' : 'password'}
                                    name="ai_api_key"
                                    value={settings.ai_api_key || ''}
                                    onChange={handleInputChange}
                                    disabled={!canModify}
                                    placeholder={aiApiKeySet ? 'Enter new value to replace' : 'Enter API key'}
                                    autoComplete="one-time-code"
                                />
                                <Button
                                    variant="outline-secondary"
                                    tabIndex={-1}
                                    onClick={() => setShowApiKey(!showApiKey)}
                                >
                                    {showApiKey ? <EyeSlashFill /> : <EyeFill />}
                                </Button>
                            </InputGroup>
                            {aiApiKeySet && (
                                <Form.Text className="text-muted">
                                    Leave blank to keep current value
                                </Form.Text>
                            )}
                        </Form.Group>

                        {(settings.ai_provider === 'venice' || settings.ai_provider === 'custom') && (
                            <Form.Group className="mb-4" controlId="ai_api_base_url">
                                <Form.Label>API Base URL</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="ai_api_base_url"
                                    value={settings.ai_api_base_url || ''}
                                    onChange={handleInputChange}
                                    disabled={!canModify}
                                    placeholder="e.g. https://api.venice.ai/api/v1"
                                    autoComplete="one-time-code"
                                    spellCheck="false"
                                />
                            </Form.Group>
                        )}
                    </Card.Body>
                </Card>

                {canModify && (
                    <Button type="submit" variant="primary">
                        Save Changes
                    </Button>
                )}
            </Form>
        </Container>
    );
}

export default SystemSettingsScreen; 